import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ReadonlyVersionInfoView } from './ReadonlyVersionInfoView';
import { useReleaseFirmware, useDeleteFirmwares } from '../../hooks';
import type { Firmware } from '../../types';
import type { EditableDropdownItem } from '@/components/EditableDropdown';

interface FirmwareSurpassedViewProps {
  modelId: number;
  firmware: Firmware;
  firmwareTypeItems: EditableDropdownItem[];
  onRolledBack: () => void;
  onDeleted: () => void;
}

export function FirmwareSurpassedView({
  modelId,
  firmware,
  firmwareTypeItems,
  onRolledBack,
  onDeleted,
}: FirmwareSurpassedViewProps) {
  const { t } = useTranslation();
  const releaseFirmware = useReleaseFirmware();
  const deleteFirmwares = useDeleteFirmwares();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const resolvedType = useMemo(
    () => firmwareTypeItems.find((item) => item.id === firmware.firmwareTypeID)?.label || firmware.firmwareTypeID.toString(),
    [firmwareTypeItems, firmware.firmwareTypeID]
  );
  const resolvedCountry = useMemo(
    () => firmware.countryID === null ? t('firmware.filters.global') : firmware.countryID,
    [firmware.countryID, t]
  );
  const isRollingBack = releaseFirmware.isPending;
  const isDeleting = deleteFirmwares.isPending;

  const handleRollback = useCallback(() => {
    releaseFirmware.mutate(
      { modelId, firmwareId: firmware.id },
      { 
        onSuccess: () => {
          setShowRollbackConfirm(false);
          onRolledBack();
        },
        onError: (error) => {
          console.error('Rollback failed:', error);
          setShowRollbackConfirm(false);
        }
      }
    );
  }, [modelId, firmware.id, releaseFirmware, onRolledBack]);

  const handleDelete = useCallback(() => {
    setDeleteError(null);
    deleteFirmwares.mutate(
      { modelId, firmwareIds: [firmware.id] },
      {
        onSuccess: () => {
          setShowDeleteConfirm(false);
          onDeleted();
        },
        onError: (error) => {
          console.error('Delete failed:', error);
          setShowDeleteConfirm(false);
          setDeleteError(t('firmware.sidePanel.deleteError'));
        },
      }
    );
  }, [modelId, firmware.id, deleteFirmwares, onDeleted, t]);

  return (
    <>
      <ErrorBanner hasError={!!deleteError}>
        {deleteError}
      </ErrorBanner>

      <ReadonlyVersionInfoView
        versionString={firmware.versionString}
        resolvedType={resolvedType}
        resolvedCountry={resolvedCountry}
        fileUploaded={firmware.fileUploaded}
      />

      <div className="pt-4 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => setShowRollbackConfirm(true)}
          disabled={isRollingBack}
          className="flex-1 py-2 px-4 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:ring-2 focus:ring-primary"
        >
          {isRollingBack ? t('firmware.sidePanel.saving') : t('firmware.sidePanel.rollbackButton')}
        </button>
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting || isRollingBack}
          className="flex-1 py-2 px-4 text-sm font-medium rounded-lg border border-error text-error hover:bg-error/10 disabled:opacity-50 disabled:pointer-events-none"
        >
          {isDeleting ? t('firmware.sidePanel.deleting') : t('firmware.sidePanel.deleteButton')}
        </button>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title={t('firmware.sidePanel.deleteConfirmTitle')}
        message={t('firmware.sidePanel.deleteConfirmMessage')}
        confirmLabel={t('firmware.sidePanel.deleteButton')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isDeleting}
        variant="danger"
      />

      <ConfirmModal
        isOpen={showRollbackConfirm}
        title={t('firmware.sidePanel.rollbackConfirmTitle')}
        message={t('firmware.sidePanel.rollbackConfirmMessage')}
        confirmLabel={t('firmware.sidePanel.rollbackButton')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleRollback}
        onCancel={() => setShowRollbackConfirm(false)}
        isLoading={isRollingBack}
      />
    </>
  );
}
